import type { ConsentStatus } from "@/core/consent/domain/consent.types";
import type { ConsentStatusStore } from "@/core/consent/engine/consent.engine";

/**
 * The page's ONE live consent engine, held at module level.
 *
 * Module-level rather than React context on purpose: the composition root
 * that creates the engine is rendered from the root layout BESIDE the app
 * tree, not around it, so a context from that position would reach no
 * consumer. `ConsentProvider` subscribes here instead and projects the
 * status onto React; anything non-React (the recorder, diagnostics) reads the
 * store directly.
 *
 * `null` whenever no engine is live: server render, before the root's effect,
 * after unmount. Consumers treat `null` as "no engine", never as a decision.
 */
let active: ConsentStatusStore | null = null;
let unsubscribeActive: (() => void) | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

export const getActiveConsentStore = (): ConsentStatusStore | null => active;

/**
 * Register (or, with `null`, release) the live engine. The composition root
 * calls this right after constructing the engine — before `start`, so the
 * very first resolution reaches React — and again from its cleanup; React
 * StrictMode's double-invocation therefore runs set → release → set, never
 * two engines side by side.
 */
export const setActiveConsentStore = (store: ConsentStatusStore | null): void => {
  unsubscribeActive?.();
  unsubscribeActive = null;
  active = store;
  if (store) {
    unsubscribeActive = store.subscribe(notify);
  }
  notify();
};

/**
 * `useSyncExternalStore`-shaped access to the engine's status. The snapshot
 * is the engine's own state reference — the reducer returns the same
 * reference for every no-op signal, which is exactly the stability React's
 * change detection needs.
 */
export const getConsentStatusSnapshot = (): ConsentStatus | null => active?.getStatus() ?? null;

export const subscribeToConsentStatus = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

let reopenBanner: (() => void) | null = null;
let pendingReopenRequest = false;

/**
 * Reopen the consent banner (or its preferences dialog) so a visitor can revisit
 * a decision — CPRA requires withdrawal to be as easy as the grant. Which CMP
 * answers is the composition root's business: it registers the opener below
 * when it mounts, so callers (`useConsent().reopenBanner`) never name a vendor.
 * A request made before the root has registered is QUEUED, not dropped, and
 * served exactly once on registration.
 */
export const reopenConsentBanner = (): void => {
  if (reopenBanner === null) {
    pendingReopenRequest = true;
    return;
  }

  reopenBanner();
};

/** Registered by the composition root; returns the unregister for its cleanup. */
export const registerConsentBannerReopener = (open: () => void): (() => void) => {
  reopenBanner = open;
  if (pendingReopenRequest) {
    pendingReopenRequest = false;
    open();
  }

  return () => {
    if (reopenBanner === open) {
      reopenBanner = null;
      pendingReopenRequest = false;
    }
  };
};
